<template>
    <div>
        <h1 class="title is-5 has-text-white">{{ $t('Access your portal') }}</h1>

        <b-notification v-if="flashMessage" type="is-success" :closable="false" class="mb-4">
            {{ flashMessage }}
        </b-notification>

        <form @submit.prevent="onSubmit" role="form">
            <!-- Step 1: Email -->
            <b-field
                v-if="step === 'email'"
                :label="$t('Email')"
                :type="form.errors.has('email') ? 'is-danger' : null"
                :message="form.errors.get('email')"
            >
                <b-input
                    ref="emailInput"
                    v-model="form.email"
                    type="email"
                    :placeholder="$t('Email')"
                    name="email"
                    required
                    autofocus
                    @keydown.enter.prevent="onEmailNext"
                    size="is-medium"
                />
            </b-field>

            <!-- SSO detected notice (shown on step 1) -->
            <div v-if="step === 'email' && ssoDetected" class="notification is-light is-info is-size-7 mb-4">
                <b-icon icon="shield-lock" size="is-small"></b-icon>
                {{ $t('Your organization uses Single Sign-On ({provider}).', { provider: ssoProviderLabel }) }}
            </div>

            <!-- Use password checkbox (shown when SSO detected but not enforced) -->
            <b-field v-if="step === 'email' && ssoDetected && !ssoEnforced">
                <b-checkbox v-model="usePassword">{{ $t('Sign in with password instead') }}</b-checkbox>
            </b-field>

            <!-- Step 1: Next button -->
            <div class="field" v-if="step === 'email'">
                <p class="control">
                    <b-button
                        type="is-primary"
                        size="is-medium"
                        @click="onEmailNext"
                        class="is-fullwidth"
                        :class="{ 'is-loading': checking }"
                        data-testid="next-btn"
                    >
                        {{
                            ssoDetected && !usePassword
                                ? $t('Sign in with {provider}', { provider: ssoProviderLabel })
                                : $t('Next')
                        }}
                    </b-button>
                </p>
            </div>

            <!-- Step 2: Password -->
            <template v-if="step === 'password'">
                <div class="is-flex is-justify-content-space-between is-align-items-center mb-4">
                    <span class="has-text-white">{{ form.email }}</span>
                    <a href="#" @click.prevent="resetToEmail" class="is-size-7">
                        {{ $t('Change') }}
                    </a>
                </div>

                <b-field
                    :label="$t('Password')"
                    :type="form.errors.has('password') ? 'is-danger' : null"
                    :message="form.errors.get('password')"
                >
                    <b-input
                        ref="passwordInput"
                        v-model="form.password"
                        type="password"
                        :placeholder="$t('Password')"
                        password-reveal
                        name="password"
                        required
                        size="is-medium"
                    />
                </b-field>

                <b-field>
                    <b-checkbox v-model="form.remember" true-value="true" false-value>
                        {{ $t('Stay signed in') }}
                    </b-checkbox>
                </b-field>

                <div class="field">
                    <p class="control">
                        <b-button
                            native-type="submit"
                            type="is-primary"
                            size="is-medium"
                            class="is-fullwidth"
                            data-testid="login-btn"
                        >
                            {{ $t('Login') }}
                        </b-button>
                    </p>
                </div>
            </template>

            <div v-if="registrationEnabled" class="field mt-4">
                <p class="control">
                    <router-link :to="{ name: 'register' }" class="button is-link is-medium is-fullwidth">
                        {{ $t('Register') }}
                    </router-link>
                </p>
            </div>

            <p class="has-text-centered">
                <router-link :to="{ name: 'lost-password' }">
                    {{ $t('Forgot your password?') }}
                </router-link>
            </p>
        </form>
    </div>
</template>

<script>
let fields = {};

export default {
    data() {
        fields = {
            email: '',
            password: '',
            remember: ''
        };

        return {
            form: new Form(fields),
            to: '/app',
            step: 'email', // 'email' | 'password'
            checking: false,
            ssoDetected: false,
            ssoEnforced: false,
            ssoProvider: null,
            ssoDomain: null,
            usePassword: false,
            lastCheckedDomain: null,
            registrationEnabled: window.config?.registrationEnabled ?? true,
            flashMessage: null
        };
    },

    computed: {
        ssoProviderLabel() {
            const labels = { google: 'Google', microsoft: 'Microsoft' };
            return labels[this.ssoProvider] || this.ssoProvider || 'SSO';
        }
    },

    created() {
        const messages = {
            'reset-link-sent': i18n.t('A password reset link has been sent to your email.')
        };
        const msg = this.$route.query.message;
        if (msg && messages[msg]) {
            this.flashMessage = messages[msg];
            // Clean up the URL
            this.$router.replace({ query: {} });
        }
    },

    mounted() {
        document.title = i18n.t('Login') + ` | ${AppTitle}`;

        // `window.intended` comes from the server session (`url.intended`) and is
        // consumed by `Session::pull` on the FIRST app.blade render — a reload of the
        // login page or the hop to /2fa would render app.blade again and lose it,
        // dropping multi-org users onto /platform/dashboard after login. Persist a real
        // intended destination into localStorage so the post-login redirect survives.
        const intended = window.intended && window.intended !== '/' ? window.intended : null;
        if (intended) {
            localStorage.setItem('redirectAfterLogin', intended);
        }

        const redirect = localStorage.getItem('redirectAfterLogin');
        if (redirect) {
            this.to = redirect;
        }
    },

    watch: {
        'form.email'() {
            clearTimeout(this._ssoCheckTimer);
            this._ssoCheckTimer = setTimeout(() => {
                this.checkSsoDomain();
            }, 400);
        }
    },

    methods: {
        async checkSsoDomain() {
            if (!this.form.email || !this.form.email.includes('@')) return;

            const domain = this.form.email.split('@')[1].toLowerCase();
            if (domain === this.lastCheckedDomain) return;

            this.lastCheckedDomain = domain;
            try {
                const response = await axios.post('/auth/sso/check', { email: this.form.email });
                if (response.data.sso) {
                    this.ssoDetected = true;
                    this.ssoEnforced = response.data.enforced || false;
                    this.ssoProvider = response.data.provider;
                    this.ssoDomain = response.data.domain;
                    this.usePassword = false;
                } else {
                    this.ssoDetected = false;
                    this.ssoEnforced = false;
                    this.ssoProvider = null;
                    this.ssoDomain = null;
                }
            } catch (e) {
                this.ssoDetected = false;
            }
        },

        async onEmailNext() {
            if (!this.form.email || !this.form.email.includes('@')) {
                this.form.errors.set('email', i18n.t('Please enter a valid email address.'));
                return;
            }

            // If we haven't checked this domain yet, check now
            const domain = this.form.email.split('@')[1].toLowerCase();
            if (domain !== this.lastCheckedDomain) {
                this.checking = true;
                await this.checkSsoDomain();
                this.checking = false;
            }

            // SSO detected and user didn't opt for password → redirect to IdP
            if (this.ssoDetected && !this.usePassword) {
                const intended = window.intended && window.intended !== '/' ? window.intended : '';
                const intentedParam = intended ? `?intended=${encodeURIComponent(intended)}` : '';
                window.location.href = `/auth/sso/${this.ssoDomain}/redirect${intentedParam}`;
                return;
            }

            // Otherwise show password field
            this.step = 'password';
            this.$nextTick(() => {
                this.$refs.passwordInput?.$el?.querySelector('input')?.focus();
            });
        },

        resetToEmail() {
            this.step = 'email';
            this.form.password = '';
            this.$nextTick(() => {
                this.$refs.emailInput?.$el?.querySelector('input')?.focus();
            });
        },

        onSubmit() {
            if (this.step === 'email') {
                this.onEmailNext();
                return;
            }

            this.form
                .post('/login')
                .then(response => {
                    if (response.data.google2fa == 1) {
                        // Refresh the <meta> for direct consumers (Echo); axios itself
                        // carries CSRF via the auto X-XSRF-TOKEN cookie, so we no longer
                        // pin a (soon-stale) X-CSRF-TOKEN default header here.
                        window.csrf_token.content = response.data.csrf_token;
                        this.$router.push('/2fa');
                    } else if (response.data.bridge_redirect) {
                        // Cross-domain bridge — redirect to custom domain with exchange code
                        window.location.href = response.data.bridge_redirect;
                    } else {
                        // `this.to` already merges window.intended (persisted in mounted)
                        // with any localStorage backup, so it is the single source of truth.
                        localStorage.removeItem('redirectAfterLogin');
                        window.location = this.to;
                    }
                })
                .catch(error => {
                    // The API returns credential errors on the 'email' field,
                    // but that field is hidden on the password step — show on password instead.
                    if (this.form.errors.has('email') && !this.form.errors.has('password')) {
                        this.form.errors.errors.password = this.form.errors.errors.email;
                    }
                });
        }
    }
};
</script>
